import { useEffect, useMemo } from "react";
import Spinner from "../../../../../components/shared/Spinner";
import Header from "./Header";
import Footer from "../../components/Footer";
import Pagination from "../../components/Pagination";
import Menu from "../../components/Menu";
import Body from "./Body";
import ScrollbarOverlay from "./ScrollbarOverlay";
import DayView from "./Body/DayView";
import CustomView from "./Body/CustomView";
import UnassignedShifts from "../../components/UnassignedShifts";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useCellWidth } from "../../hooks/grid/useCellWidth";
import { useCurrentTimeOffset } from "../../hooks/grid/useCurrentTimeOffset";
import { usePrintScale } from "../../hooks/grid/usePrintScale";
import { useDragAndDropCopyModifier } from "../../hooks/useDragAndDropCopyModifier";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useScheduleApi } from "../../api/useScheduleApi";
import { cn } from "../../../../../lib/utils";

function Grid({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal
}: any) {
    const cellWidth = useCellWidth();
    const currentTimeOffset = useCurrentTimeOffset({ cellWidth });
    const [filters] = useFilters();
    const {
        weeklySummary,
        resourceLayouts,
        eventLayouts,
        resources,
        events,
        loading,
        loadingOpenShifts,
        loadingUnassignedEvents,
        type,
        showUnassigendShifts,
    } = useScheduleContext();
    const { getUnassignedShifts } = useScheduleApi()
    useDragAndDropCopyModifier();
    usePrintScale("printable-schedule-content");

    const visibleEvents = useMemo(() => (
        filters?.mode === "day"
            ? events
            : events?.filter((event: any) => event?.type !== "unavailable")
    ), [filters?.mode, events]);

    const content = useMemo(() => {
        const Component = filters?.mode === "day" ? DayView : CustomView;
        return (
            <Component
                resources={resources}
                events={visibleEvents}
                resourceLayouts={resourceLayouts ?? {}}
                eventLayouts={eventLayouts ?? {}}
                cellWidth={cellWidth}
            />
        );
    }, [
        filters?.mode,
        resources,
        visibleEvents,
        resourceLayouts,
        eventLayouts,
        cellWidth,
    ]);

    useEffect(() => {
        if (!showUnassigendShifts) return;
        const controller = new AbortController();
        getUnassignedShifts(filters, controller);
        return () => controller.abort();
    }, [showUnassigendShifts, getUnassignedShifts, filters]);

    return (
        <>
            <div className="tw-flex tw-h-full">
                <div
                    id="printable-schedule-content"
                    className={
                        cn(
                            "schedule-content",
                            ["team", "template"].includes(type) && "team-content-height"
                        )}
                >
                    <div className="scheduler-container" style={{ maxHeight: "fit-content" }}>
                        <Header
                            cellWidth={cellWidth}
                            currentTimeOffset={currentTimeOffset}
                        />

                        {(loading || loadingOpenShifts || loadingUnassignedEvents) ? (
                            <div className="schedule-loading-container">
                                <Spinner className="tw-text-primary" />
                            </div>
                        ) : (
                            <>
                                <Body
                                    currentTimeOffset={currentTimeOffset}
                                    cellWidth={cellWidth}
                                >
                                    {content}
                                </Body>
                                <ScrollbarOverlay />
                            </>
                        )}
                    </div>

                    {weeklySummary && <Footer />}
                </div>

                {showUnassigendShifts && (
                    <UnassignedShifts
                        openAssignModal={openAssignModal}
                        openDeleteModal={openDeleteModal}
                        cellWidth={cellWidth}
                    />
                )}
            </div>

            {["team", "template"].includes(type) && <Pagination />}

            <Menu
                openActionsModal={openActionsModal}
                openCopyModal={openCopyModal}
                openDeleteModal={openDeleteModal}
                openAssignModal={openAssignModal}
                openProfileSheet={openProfileSheet}
                openTransferModal={openTransferModal}
                openShiftTradeDrawer={openShiftTradeDrawer}
                openClaimShiftDrawer={openClaimShiftDrawer}
                openSaveDayAsTemplate={openSaveDayAsTemplate}
                openLoadTemplatesModal={openLoadTemplatesModal}
                openMoveToOpenShiftsModal={openMoveToOpenShiftsModal}
            />
        </>
    );
}

export default Grid;
